import { cn } from '@/lib/cn';
import { Waves, RefreshCw, Bell, ClipboardCheck } from 'lucide-react';

interface FarmStatsProps {
  totalPonds: number;
  activeCycles: number;
  pendingAlerts: number;
  readingsToday: number;
}

export function FarmStats({
  totalPonds,
  activeCycles,
  pendingAlerts,
  readingsToday,
}: FarmStatsProps) {
  const stats = [
    {
      name: 'Total Ponds',
      value: totalPonds,
      icon: Waves,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      accent: 'border-l-blue-500',
    },
    {
      name: 'Active Cycles',
      value: activeCycles,
      icon: RefreshCw,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      accent: 'border-l-emerald-500',
    },
    {
      name: 'Pending Alerts',
      value: pendingAlerts,
      icon: Bell,
      iconBg: pendingAlerts > 0 ? 'bg-red-100' : 'bg-gray-100',
      iconColor: pendingAlerts > 0 ? 'text-red-600' : 'text-gray-500',
      accent: pendingAlerts > 0 ? 'border-l-red-500' : 'border-l-gray-300',
      highlight: pendingAlerts > 0,
    },
    {
      name: 'Readings Today',
      value: readingsToday,
      icon: ClipboardCheck,
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      accent: 'border-l-violet-500',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat) => (
        <div
          key={stat.name}
          className={cn(
            'relative bg-white rounded-xl border border-gray-200/80',
            'shadow-sm shadow-gray-900/5',
            'border-l-4',
            stat.accent,
            'p-4 sm:p-5',
            'transition-all duration-200',
            'hover:shadow-md hover:border-gray-300',
            stat.highlight && 'ring-2 ring-red-100'
          )}
        >
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'flex items-center justify-center rounded-lg p-2.5',
                stat.iconBg,
                'transition-transform duration-200 group-hover:scale-110'
              )}
            >
              <stat.icon className={cn('h-5 w-5 sm:h-6 sm:w-6', stat.iconColor)} />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={cn(
                  'text-2xl sm:text-3xl font-bold tracking-tight',
                  stat.highlight ? 'text-red-600' : 'text-gray-900'
                )}
              >
                {stat.value}
              </p>
              <p className="text-sm text-gray-500 mt-0.5 truncate">{stat.name}</p>
            </div>
          </div>

          {/* Subtle pulse animation for alerts */}
          {stat.highlight && (
            <div className="absolute top-3 right-3">
              <span className="flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
